import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  CheckCheck,
  Lock,
  MessageCircle,
  MessageSquarePlus,
  Reply as ReplyIcon,
  Send,
  StickyNote,
} from 'lucide-react';
import { toast } from 'sonner';
import type {
  InboxStatus,
  InboxThread,
  InboxThreadDetail,
} from '../../types/inbox';
import type { CurrentUser } from '../../types/user';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import { Textarea } from '../../components/ui/textarea';
import { SelectField } from '../../components/ui/SelectField';
import { formatRelativeTime } from '../../lib/relativeTime';
import { useLiveRefreshOnOpen } from '../../lib/useLiveRefreshOnOpen';
import {
  canSendPrivately,
  CHANNEL_LABEL,
  formatExactTimestamp,
  replySendArgs,
  type ReplyTarget,
} from '../../lib/inboxView';
import { ConnectionExpiredChip } from '../connectionHealth';
import { isClaimedByOther } from '../../lib/inboxClaim';
import { useCurrentUser } from '../auth';
import { ChannelIcon } from '../../components/ui/ChannelIcon';
import { PostContextHeader, PostPreviewPanel } from './PostPreviewPanel';
import { PostGroupComment } from './PostGroupComment';
import { ReviewDetailBody } from './ReviewDetail';
import { DmDetailBody } from './DmDetail';
import {
  addThreadNote,
  assignThread,
  claimThread,
  deleteComment,
  fetchPostGroup,
  fetchThreadDetail,
  hideComment,
  likeComment,
  refreshThreadLive,
  releaseThreadClaim,
  replyToThread,
  resolvePostComments,
  setThreadPriority,
  setThreadStatus,
  unhideComment,
  unlikeComment,
} from './api';
import { buildCommentTree, flattenCommentTree } from '../../lib/commentTree';
import { PriorityToggle } from './threadDetailShared';
import { SuggestReplyPanel } from './SuggestReplyPanel';
import { commentBodyMessage, DELETE_COMMENT_CONFIRM } from '../../lib/inboxRowActions';
import { AccountLinkIcons } from '../../components/ui/AccountLinkIcons';
import type { AccountExternalLinks } from '../../types/account';

// Community Inbox Phase 1.5 — restructured thread detail (mirrors Zernio's
// clarity): post-context header up top, the comments feed scrolling below,
// per-comment Like / Reply / Hide / Delete row actions, and a composer DOCKED
// to the bottom of the pane (no more scroll-to-the-end) with an explicit
// reply target ("Replying to <author>" vs "Reply to post"). Internal notes
// collapse behind a toggle next to the composer. Polls every 15s while open.

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
];

export function ThreadDetail({
  threadId,
  users,
  accountNameById,
  accountLinksById,
  onSelectThread,
}: {
  threadId: string;
  users: CurrentUser[];
  accountNameById: Map<string, string>;
  // The account's external links (website / FB / IG / GBP / ad account) for
  // the header icons. A missing entry simply renders no icons.
  accountLinksById: Map<string, AccountExternalLinks>;
  // Part B — the detail shows the whole post group; picking another comment
  // makes it the active (reply/resolve) target.
  onSelectThread: (threadId: string) => void;
}) {
  const me = useCurrentUser();
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ['community-thread', threadId],
    queryFn: () => fetchThreadDetail(threadId),
    refetchInterval: 15_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['community-thread', threadId] });
    void queryClient.invalidateQueries({ queryKey: ['community-threads'] });
    void queryClient.invalidateQueries({ queryKey: ['community-worklist'] });
    // The post GROUP drives the conversation view — without this, a newly sent
    // comment (especially our own top-level one, which becomes its own thread)
    // wouldn't show until the next 15s poll.
    void queryClient.invalidateQueries({ queryKey: ['community-post-group'] });
  };

  // Refresh-on-open: pull native Facebook / Instagram replies for this thread live.
  useLiveRefreshOnOpen({
    scope: 'staff',
    threadId,
    enabled: !!detail.data && detail.data.thread.kind !== 'review',
    refresh: refreshThreadLive,
    onNewMessages: invalidate,
  });

  if (detail.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-slate-500">
        Couldn't load this thread.
      </div>
    );
  }

  // Kind-aware body (Phase 2): reviews + DMs get their own detail views; the
  // comment body is unchanged.
  const kind = detail.data.thread.kind;
  const bodyProps = {
    detail: detail.data,
    me,
    users,
    accountNameById,
    accountLinksById,
    onChanged: invalidate,
  };
  if (kind === 'review') return <ReviewDetailBody key={threadId} {...bodyProps} />;
  if (kind === 'dm') return <DmDetailBody key={threadId} {...bodyProps} />;
  return <ThreadDetailBody key={threadId} {...bodyProps} onSelectThread={onSelectThread} />;
}

function ThreadDetailBody({
  detail,
  me,
  users,
  accountNameById,
  accountLinksById,
  onChanged,
  onSelectThread,
}: {
  detail: InboxThreadDetail;
  me: CurrentUser;
  users: CurrentUser[];
  accountNameById: Map<string, string>;
  accountLinksById: Map<string, AccountExternalLinks>;
  onChanged: () => void;
  onSelectThread: (threadId: string) => void;
}) {
  const { thread, messages, notes } = detail;
  const [draft, setDraft] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  // Part B — the post's whole conversation. Cached per POST so switching
  // between comments on the same post reuses it.
  const postGroup = useQuery({
    queryKey: [
      'community-post-group',
      thread.kyckAccountId,
      thread.platformPostId ?? thread.id,
    ],
    queryFn: () => fetchPostGroup(thread.id),
    refetchInterval: 15_000,
    placeholderData: (prev) => prev,
  });
  const commentNodes = useMemo(
    () => flattenCommentTree(buildCommentTree(postGroup.data?.entries ?? [])),
    [postGroup.data],
  );
  const unresolvedInPost = postGroup.data?.unresolvedCount ?? 0;

  // B1/B2 — the composer's target. DEFAULT = the comment you arrived from;
  // switchable to any other comment in the post (its Reply action) or to a
  // top-level "Comment on post". `null` = follow the default below.
  const [targetOverride, setTargetOverride] = useState<ReplyTarget | null>(null);
  const defaultTarget = useMemo<Extract<ReplyTarget, { kind: 'comment' }> | null>(() => {
    // The SAME selector the thread row's Hide/Delete uses, so the row can never
    // act on a different message from the one this pane targets.
    const body = commentBodyMessage(messages);
    return body
      ? { kind: 'comment', threadId: thread.id, messageId: body.id, authorName: body.authorName }
      : null;
  }, [messages, thread.id]);
  // Falls back to a post-level comment when the selected thread has no
  // replyable body (e.g. a media-only comment we couldn't ingest text for).
  const replyTarget: ReplyTarget = targetOverride ?? defaultTarget ?? { kind: 'post' };
  // Selecting a different comment in the group resets the override so the
  // composer follows the newly-selected comment.
  useEffect(() => {
    setTargetOverride(null);
  }, [thread.id]);
  const [noteDraft, setNoteDraft] = useState('');
  const claimHeld = useRef(false);

  const claimedByOther = isClaimedByOther(thread, me.uid, Date.now());
  const claimHolder = claimedByOther ? users.find((u) => u.uid === thread.claimedByUid) : null;

  useEffect(() => {
    return () => {
      if (claimHeld.current) {
        claimHeld.current = false;
        void releaseThreadClaim(thread.id).catch(() => undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.id]);

  const onComposerFocus = () => {
    if (claimHeld.current) return;
    void claimThread(thread.id)
      .then((res) => {
        claimHeld.current = res.claimed;
      })
      .catch(() => undefined);
  };

  const reply = useMutation({
    mutationFn: () => {
      const args = replySendArgs(replyTarget, thread.id);
      return replyToThread(args.threadId, {
        message: draft.trim(),
        isPrivate,
        replyToMessageId: args.replyToMessageId,
      });
    },
    onSuccess: () => {
      setDraft('');
      setIsPrivate(false);
      setTargetOverride(null);
      claimHeld.current = false; // send releases the claim server-side
      toast.success(
        isPrivate
          ? 'Private reply sent.'
          : replyTarget.kind === 'post'
            ? 'Comment posted.'
            : 'Reply sent — comment resolved.',
      );
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const onSend = () => {
    if (draft.trim() === '' || reply.isPending) return;
    if (isPrivate && !canSendPrivately(replyTarget)) {
      toast.error('Pick a comment to reply to privately (use Reply on a comment).');
      return;
    }
    if (
      claimedByOther &&
      !window.confirm(
        `${claimHolder?.displayName ?? 'Another agent'} is already replying to this thread. Send anyway?`,
      )
    ) {
      return;
    }
    reply.mutate();
  };

  // Part B — moderation acts on a SPECIFIC comment-thread's message (the pane
  // shows the whole post group, so the target thread varies per row).
  type MsgTarget = { threadId: string; messageId: string };
  const like = useMutation({
    mutationFn: ({ threadId, messageId, liked }: MsgTarget & { liked: boolean }) =>
      liked ? unlikeComment(threadId, messageId) : likeComment(threadId, messageId),
    onSuccess: onChanged,
    onError: (err: Error) => toast.error(err.message),
  });
  const hide = useMutation({
    mutationFn: ({ threadId, messageId }: MsgTarget) => hideComment(threadId, messageId),
    onSuccess: () => {
      toast.success('Comment hidden.');
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const unhide = useMutation({
    mutationFn: ({ threadId, messageId }: MsgTarget) => unhideComment(threadId, messageId),
    onSuccess: () => {
      toast.success('Comment unhidden.');
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const remove = useMutation({
    mutationFn: ({ threadId, messageId }: MsgTarget) => deleteComment(threadId, messageId),
    onSuccess: () => {
      toast.success('Comment deleted.');
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  // Per-comment resolve tick (Part B item 4) — resolve one comment in the group.
  const resolveOne = useMutation({
    mutationFn: (threadId: string) => setThreadStatus(threadId, 'resolved'),
    onSuccess: () => {
      toast.success('Comment resolved.');
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  // Resolve ALL unresolved comments on this post (each audited server-side).
  const resolveAll = useMutation({
    mutationFn: () => resolvePostComments(thread.id),
    onSuccess: (res) => {
      toast.success(`Resolved ${res.resolved} comment${res.resolved === 1 ? '' : 's'}.`);
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const assign = useMutation({
    mutationFn: (assigneeUid: string | null) => assignThread(thread.id, assigneeUid),
    onSuccess: onChanged,
    onError: (err: Error) => toast.error(err.message),
  });
  const status = useMutation({
    mutationFn: (next: InboxStatus) => setThreadStatus(thread.id, next),
    onSuccess: onChanged,
    onError: (err: Error) => toast.error(err.message),
  });
  const priority = useMutation({
    mutationFn: (next: boolean) => setThreadPriority(thread.id, next),
    onSuccess: onChanged,
    onError: (err: Error) => toast.error(err.message),
  });
  const note = useMutation({
    mutationFn: () => addThreadNote(thread.id, noteDraft.trim()),
    onSuccess: () => {
      setNoteDraft('');
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const assigneeOptions = [
    { value: '', label: 'Unassigned' },
    ...users.map((u) => ({ value: u.uid, label: u.displayName || u.email })),
  ];

  return (
    // min-h-0 so this pane shrinks to the bounded column row rather than being
    // sized by the (potentially very long) conversation inside it.
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200">
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 bg-slate-50 px-4 py-3">
        <div className="flex items-center gap-2">
          <ChannelIcon channel={thread.channel} />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">
            {thread.author.name}
          </span>
          {thread.isAd && (
            <Badge variant="outline" className="border-purple-200 bg-purple-50 text-purple-700">
              Ad comment
            </Badge>
          )}
          <PriorityToggle
            priority={thread.priority === true}
            disabled={priority.isPending}
            onToggle={() => priority.mutate(thread.priority !== true)}
          />
          {thread.status !== 'resolved' && (
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              title="Resolve just this comment"
              disabled={status.isPending}
              onClick={() => status.mutate('resolved')}
            >
              <Check className="h-3.5 w-3.5 text-kyck-600" />
              Resolve
            </Button>
          )}
          {/* Part B — resolve every unresolved comment on this post at once. */}
          {unresolvedInPost > 1 && (
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              title={`Resolve all ${unresolvedInPost} unresolved comments on this post`}
              disabled={resolveAll.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Resolve all ${unresolvedInPost} unresolved comments on this post?`,
                  )
                ) {
                  resolveAll.mutate();
                }
              }}
            >
              <CheckCheck className="h-3.5 w-3.5 text-kyck-600" />
              Resolve all ({unresolvedInPost})
            </Button>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2">
          <p className="text-xs text-slate-500">
            {accountNameById.get(thread.kyckAccountId) ?? thread.kyckAccountId} ·{' '}
            {CHANNEL_LABEL[thread.channel]}
          </p>
          <AccountLinkIcons links={accountLinksById.get(thread.kyckAccountId)} />
          {/* A dead token here means this account's comments/reviews/DMs have
              silently stopped — surface it so an expired connection doesn't read
              as "no activity". */}
          {/* Part E — scoped to THIS thread's platform so an unrelated failure
              (e.g. Google Business) never surfaces on an IG/FB thread. */}
          <ConnectionExpiredChip
            accountId={thread.kyckAccountId}
            channel={thread.channel}
            compact
          />
        </div>
        <div className="mt-2 flex items-center gap-2">
          <SelectField
            size="sm"
            aria-label="Status"
            value={thread.status}
            options={STATUS_OPTIONS}
            onValueChange={(v) => status.mutate(v as InboxStatus)}
          />
          <SelectField
            size="sm"
            aria-label="Assignee"
            value={thread.assigneeUid ?? ''}
            options={assigneeOptions}
            placeholder="Assign…"
            onValueChange={(v) => assign.mutate(v === '' ? null : v)}
          />
        </div>
      </div>

      {/* Compact post strip — narrow viewports only; at xl+ the large
          right-hand PostPreviewPanel carries the post context instead. */}
      <div className="shrink-0 xl:hidden">
        <PostContextHeader thread={thread} />
      </div>

      {/* Collision banner */}
      {claimedByOther && (
        <div className="flex shrink-0 items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
          <Lock className="h-3.5 w-3.5" />
          {(claimHolder?.displayName || claimHolder?.email) ?? 'Another agent'} is replying…
        </div>
      )}

      {/* Two columns (post-preview-fix §2): conversation LEFT, large post
          preview RIGHT (xl+ — fills the previously-dead right space). */}
      <div className="flex min-h-0 flex-1">
        {/* The CONVERSATION column: comment list scrolls, composer pinned.
            min-h-0 at every flex level is load-bearing — one missing link and
            the column is sized by its content, so the composer lands at the
            bottom of a very tall page instead of the screen. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/* Part B — the whole POST's conversation: every comment on the post
              is its own resolvable item, indented under its parent for context.
              The selected one is highlighted; clicking another selects it.
              This is the ONLY scroll region in the column. */}
          <div className="scroll-subtle min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-3">
            {commentNodes.map((node) => (
              <PostGroupComment
                key={node.entry.thread.id}
                node={node}
                priorityFlag={node.entry.thread.priority === true}
                selected={node.entry.thread.id === thread.id}
                isReplyTarget={
                  replyTarget.kind === 'comment' &&
                  replyTarget.threadId === node.entry.thread.id
                }
                onSelect={() => onSelectThread(node.entry.thread.id)}
                onReply={(m) =>
                  setTargetOverride({
                    kind: 'comment',
                    threadId: node.entry.thread.id,
                    messageId: m.id,
                    authorName: m.authorName,
                  })
                }
                onResolve={() => resolveOne.mutate(node.entry.thread.id)}
                resolving={resolveOne.isPending}
                likePending={like.isPending}
                onLike={(m) =>
                  like.mutate({
                    threadId: node.entry.thread.id,
                    messageId: m.id,
                    liked: m.liked ?? false,
                  })
                }
                onHide={(m) => hide.mutate({ threadId: node.entry.thread.id, messageId: m.id })}
                onUnhide={(m) => unhide.mutate({ threadId: node.entry.thread.id, messageId: m.id })}
                onDelete={(m) => {
                  if (window.confirm(DELETE_COMMENT_CONFIRM)) {
                    remove.mutate({ threadId: node.entry.thread.id, messageId: m.id });
                  }
                }}
              />
            ))}
            {commentNodes.length === 0 && !postGroup.isLoading && (
              <p className="py-6 text-center text-sm text-slate-500">No comments on this post.</p>
            )}
          </div>

      {/* Internal notes — collapsible, docked above the composer */}
      {notesOpen && (
        <div className="shrink-0 border-t border-slate-200 bg-amber-50/40 px-4 py-3">
          <div className="scroll-subtle max-h-36 space-y-2 overflow-y-auto">
            {notes.map((n) => (
              <div key={n.id} className="rounded-md bg-amber-50 px-2.5 py-1.5 text-sm text-slate-700">
                <span className="font-medium">{n.authorName}</span>{' '}
                <span className="text-xs text-slate-400" title={formatExactTimestamp(n.createdAt)}>
                  {formatRelativeTime(n.createdAt)}
                </span>
                <p className="whitespace-pre-wrap">{n.text}</p>
              </div>
            ))}
            {notes.length === 0 && (
              <p className="text-xs text-slate-500">No internal notes yet.</p>
            )}
          </div>
          <div className="mt-2 flex items-start gap-2">
            <Textarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Add an internal note (never sent to the customer)…"
              rows={1}
              className="min-h-9"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => note.mutate()}
              disabled={noteDraft.trim() === '' || note.isPending}
            >
              Add
            </Button>
          </div>
        </div>
      )}

      {/* Docked composer (Phase 1.5 §E; Part D5 sticky) */}
      <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-3">
        {/* B1 — explicit reply target. Reply-to-comment stays the default; the
            toggle restores posting a TOP-LEVEL comment on the post's main
            thread (lost in the per-comment split). Use Reply on any comment
            above (B2) to retarget without leaving the page. */}
        <div className="mb-1.5 flex items-center gap-2 text-xs">
          {replyTarget.kind === 'comment' ? (
            <>
              <span className="inline-flex items-center gap-1 rounded-full bg-kyck-50 px-2 py-0.5 text-kyck-800">
                <ReplyIcon className="h-3 w-3" />
                Replying to {replyTarget.authorName}
              </span>
              <button
                type="button"
                onClick={() => {
                  setTargetOverride({ kind: 'post' });
                  setIsPrivate(false); // a private reply needs a comment target
                }}
                className="text-slate-500 underline-offset-2 hover:text-kyck-700 hover:underline"
              >
                Comment on post instead
              </button>
            </>
          ) : (
            <>
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                <MessageSquarePlus className="h-3 w-3" />
                New comment on the post
              </span>
              {defaultTarget && (
                <button
                  type="button"
                  onClick={() => setTargetOverride(null)}
                  className="text-slate-500 underline-offset-2 hover:text-kyck-700 hover:underline"
                >
                  Reply to {defaultTarget.authorName} instead
                </button>
              )}
            </>
          )}
        </div>
        {/* AI drafts for the CURRENT reply target. Hidden when the target is a
            new top-level comment on the post: that answers nobody in
            particular, so there is no item to draft a reply to. */}
        <SuggestReplyPanel
          className="mb-2"
          threadId={replySendArgs(replyTarget, thread.id).threadId}
          messageId={replySendArgs(replyTarget, thread.id).replyToMessageId}
          disabled={replyTarget.kind !== 'comment'}
          onUse={setDraft}
        />
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={onComposerFocus}
          placeholder={
            isPrivate
              ? 'Write a private reply (sent as a DM)…'
              : replyTarget.kind === 'comment'
                ? `Reply to ${replyTarget.authorName}…`
                : 'Write a new comment on the post…'
          }
          rows={2}
        />
        <div className="mt-2 flex items-center gap-2">
          <Button
            variant={isPrivate ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setIsPrivate((p) => !p)}
            disabled={!canSendPrivately(replyTarget) && !isPrivate}
            title={
              canSendPrivately(replyTarget)
                ? 'Reply privately (comment → DM) instead of a public comment'
                : 'Private replies target a specific comment — switch the target to a comment first'
            }
          >
            <MessageCircle className="h-3.5 w-3.5" />
            {isPrivate ? 'Private reply' : 'Public reply'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setNotesOpen((o) => !o)}
            title="Internal notes (never sent to the customer)"
          >
            <StickyNote className="h-3.5 w-3.5" />
            Notes ({notes.length})
          </Button>
          <span className="flex-1" />
          <Button size="sm" onClick={onSend} disabled={draft.trim() === '' || reply.isPending}>
            {reply.isPending ? <Spinner className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
            Send
          </Button>
        </div>
      </div>
        </div>

        {/* Large post preview — the right column (xl+). */}
        <PostPreviewPanel
          thread={thread}
          className="hidden w-[380px] shrink-0 xl:block 2xl:w-[440px]"
        />
      </div>
    </div>
  );
}
